import { useMemo, useRef } from "react";
import { useDataChannel } from "@livekit/components-react";
import { ADMIT_POLICY_CHANGED_TYPE, parseRoomDataMessage } from "../lib/roomDataMessages";

/**
 * Calls `onChange(newPolicy)` when the backend announces that the room's
 * admit_policy changed (#1850). Room polling is off during a call, so this is
 * how someone already in it learns they may — or may no longer — manage the
 * waiting list. `newPolicy` is undefined if the message omits it.
 *
 * Must be called inside a <LiveKitRoom> provider.
 */
export function useAdmitPolicyChanges(onChange: (policy: string | undefined) => void): void {
  const decoder = useMemo(() => new TextDecoder(), []);
  // Latest callback without re-subscribing the data channel on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useDataChannel((msg) => {
    const data = parseRoomDataMessage(decoder, msg.payload);
    if (data?.type !== ADMIT_POLICY_CHANGED_TYPE) return;
    onChangeRef.current(typeof data.admit_policy === "string" ? data.admit_policy : undefined);
  });
}
